import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import { GenNode } from '../types/carbon/models/GenNode'
import { SpecAggregate } from '../types/carbon-legacy/SpecAggregate'
import {
  API_BASE_URL,
} from '../constants/system'
import { calculateVartreeParentPaths, processNodes, getAllDescendantIds, getNodeMap, getAncestorFolderIds, getAncestorFolderIdsFromPath, findNode } from '../utils/tree'
import { extractErrorMessage } from '../utils/apitools'
import { getNodeID } from '../utils/nodeTypes'
import useAccount from './useAccount'
import { initiallyOpenFolderIds } from '../components/tree/utils'

export const useVariableTree = ({
  customer,
  job,
  driver,
  autoExpandVariables = true,
  autoCheckOnSelect = false,
  singleVariable = false,
  loadCodes = true,
  initialVariableNames = [],
  initialFolderPath,
  nodeFilter,
  onSelectedVariablesChange = () => {},
}: {
  customer: string,
  job: string,
  driver?: 'carbon' | 'autocoder',
  autoExpandVariables?: boolean,
  autoCheckOnSelect?: boolean,
  singleVariable?: boolean,
  loadCodes?: boolean,
  initialVariableNames?: string[],
  initialFolderPath?: string,
  nodeFilter?: (node: GenNode) => boolean,
  onSelectedVariablesChange?: (selected: GenNode[], nodeMap: Record<string, GenNode>) => void,
}) => {
  const account = useAccount()
  const [checked, setChecked] = useState<string[]>([])
  const [expanded, setExpanded] = useState<string[]>([])
  const [codes, setCodes] = useState<Record<string, GenNode[]>>({})
  const [codeParents, setCodeParents] = useState<Record<string, string>>({})
  const [loadingCodes, setLoadingCodes] = useState<Set<string>>(new Set())
  const hasInitializedExpansion = useRef(false)
  // Code loads in flight, by node id, so a second ask (a tick while the
  // chevron's load is still running, or the other way round) waits for the same
  // answer instead of getting an empty list. The state copies above only drive
  // the render; a handler closed over an older render cannot be trusted to
  // read them. `codesGeneration` drops answers that arrive after a reset.
  const codeLoads = useRef(new Map<string, Promise<GenNode[]>>())
  const loadedCodes = useRef(new Map<string, GenNode[]>())
  const codesGeneration = useRef(0)
  // Variables ticked before their codes arrived. Unticking removes the entry,
  // so a late answer does not tick them again.
  const pendingTicks = useRef(new Set<string>())

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['vartree', {
      customer,
      job,
      driver,
      user: account.user?.session_info?.email,
    }],
    queryFn: async () => {
      if(!customer || !job || !account.user) return null
      try {
        // AutoCoder uses stateless endpoint, Carbon uses session-based flow
        const url = driver === 'autocoder'
          ? `${API_BASE_URL}/job/${customer}/${job}/vartree?storageDriver=autocoder`
          : `${API_BASE_URL}/job/${customer}/${job}/vartree`
        const res = await axios.get<SpecAggregate>(url)
        return res.data.variableTree
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  const createPlaceholderChild = (variableId: string): GenNode => ({
    type: 'Loading',
    id: 0,
    parentId: 0,
    isExpanded: false,
    isFolder: false,
    value1: `${variableId}: ${loadingCodes.has(variableId) ? 'Loading...' : 'Click to load codes'}`,
    value2: '',
    level: 0,
    anyChildren: false,
    children: [],
    meta: []
  })

  // Helper function to detect if children contain any CodeFrame nodes
  const containsCodeFrames = (children: GenNode[] | undefined | null): boolean => {
    if (!children || children.length === 0) return false
    return children.some(child => child.type === 'Codeframe')
  }

  // Loads children for Variable or CodeFrame nodes
  const loadCodesForVariable = (node: GenNode): Promise<GenNode[]> => {
    const nodeId = getNodeID(node)
    const loaded = loadedCodes.current.get(nodeId)
    if (loaded) return Promise.resolve(loaded)
    const inFlight = codeLoads.current.get(nodeId)
    if (inFlight) return inFlight

    if(!loadCodes) return Promise.resolve([])

    const generation = codesGeneration.current
    const isCurrent = () => generation === codesGeneration.current
    setLoadingCodes(prev => new Set([...prev, nodeId]))

    const load = (async (): Promise<GenNode[]> => {
      try {
        // AutoCoder uses stateless endpoint, Carbon uses session-based flow
        const baseUrl = `${API_BASE_URL}/job/${customer}/${job}/vartree/codes?variable_id=${encodeURIComponent(node.value1 || '')}`
        const url = driver === 'autocoder' ? `${baseUrl}&storageDriver=autocoder` : baseUrl
        const res = await axios.get<GenNode[]>(url)
        const item = res.data[0]
        const loadedChildren = item?.children || []
        if (isCurrent()) {
          loadedCodes.current.set(nodeId, loadedChildren)
          setCodes(prev => ({ ...prev, [nodeId]: loadedChildren }))
          setCodeParents(prev => {
            const newValues = loadedChildren.reduce((acc: Record<string, string>, child) => {
              acc[getNodeID(child)] = nodeId
              return acc
            }, {})
            return { ...prev, ...newValues }
          })
        }
        return loadedChildren
      } catch (error) {
        console.error('Failed to load children:', error)
        // Set empty array to indicate loading failed
        if (isCurrent()) {
          loadedCodes.current.set(nodeId, [])
          setCodes(prev => ({ ...prev, [nodeId]: [] }))
        }
        return []
      } finally {
        if (isCurrent()) {
          codeLoads.current.delete(nodeId)
          setLoadingCodes(prev => {
            const newSet = new Set(prev)
            newSet.delete(nodeId)
            return newSet
          })
        }
      }
    })()
    codeLoads.current.set(nodeId, load)
    return load
  }

  const nodes = useMemo(() => {
    let sourceData = data || []

    // Apply node filter if provided
    if (nodeFilter) {
      sourceData = processNodes(sourceData, (node) => {
        // Keep node if it passes the filter
        if (!nodeFilter(node)) {
          return undefined
        }
        // For folders, recursively filter children
        if (node.type === 'Folder' && node.children) {
          return {
            ...node,
            children: node.children.filter(nodeFilter)
          }
        }
        return node
      }).filter(node => node !== undefined) as GenNode[]
    }

    const withParentPaths = calculateVartreeParentPaths(sourceData)
    const withChildren = processNodes(withParentPaths, (node) => {
      // Handle both Variable and CodeFrame nodes (they both have lazy-loaded children)
      if(node.type !== 'Variable' && node.type !== 'Codeframe') return node

      if(!loadCodes) {
        return {
          ...node,
          hasChildren: false,
          children: [],
        }
      }

      const nodeId = getNodeID(node)
      const loadedChildren = codes[nodeId]

      if (loadedChildren) {
        // Already loaded children
        return {
          ...node,
          children: loadedChildren,
        }
      } else {
        // Not loaded yet - show placeholder to make it expandable
        return {
          ...node,
          children: [createPlaceholderChild(nodeId)],
        }
      }
    })
    return withChildren
  }, [data, codes, loadingCodes, loadCodes, nodeFilter])

  const nodeMap = useMemo(() => getNodeMap(nodes), [nodes])

  const onNodeExpandToggle = async (itemId: string, node: GenNode, isExpanded: boolean) => {
    setExpanded(prev => {
      if(isExpanded) {
        return [...prev, itemId]
      } else {
        return prev.filter(id => id !== itemId)
      }
    })
    if(!isExpanded) return

    // If expanding a Variable or CodeFrame node, trigger async loading of its children
    if ((node.type === 'Variable' || node.type === 'Codeframe') && isExpanded) {
      await loadCodesForVariable(node)
    }
  }

  const getCheckboxState = (node: GenNode) => {
    if (node.type === 'Folder') {
      return {
        enabled: false,
        checked: false,
      }
    }

    // Special case: Variables containing CodeFrames should be disabled (act like folders)
    if (node.type === 'Variable') {
      // Check the node's children directly (already populated by nodes memo)
      // Filter out placeholder Loading children to get real loaded children
      const realChildren = node.children?.filter(child => child.type !== 'Loading') || []

      // If real children exist and contain CodeFrames, disable the Variable checkbox
      if (realChildren.length > 0 && containsCodeFrames(realChildren)) {
        return {
          enabled: false,
          checked: false,
        }
      }
    }

    return {
      enabled: true,
      checked: checked.includes(getNodeID(node)),
    }
  }

  const onNodeCheckedToggle = async (itemId: string, node: GenNode, isChecked: boolean) => {
    let useNode = node
    let useCodeFrameNode = node

    // if we check a Variable, expand it and load children
    if(node.type == 'Variable') {
      if(!isChecked) pendingTicks.current.delete(getNodeID(node))
      else if(autoExpandVariables) {
        setExpanded(prev => prev.includes(itemId) ? prev : [...prev, itemId])
        if(!node.children || node.children.length === 0 || (node.children.length === 1 && node.children[0].type === 'Loading')) {
          // The codes can take a while (an AutoCoder job reads them from its
          // data), so the box ticks now and the codes follow. Without this the
          // click seems to do nothing, and a second click lands on a load
          // that is still running.
          const nodeId = getNodeID(node)
          pendingTicks.current.add(nodeId)
          setChecked(prev => prev.includes(nodeId) ? prev : [...prev, nodeId])
          const loadedChildren = await loadCodesForVariable(node)
          if(!pendingTicks.current.delete(nodeId)) return // unticked meanwhile
          useNode = {
            ...node,
            children: loadedChildren,
          }

          // Check if loaded children contain CodeFrames
          if (containsCodeFrames(loadedChildren)) {
            // Variable contains CodeFrames - treat it like a folder, don't check anything
            setChecked(prev => prev.filter(id => id !== nodeId))
            return
          }
        } else {
          // Children already loaded, check if they contain CodeFrames
          if (containsCodeFrames(useNode.children)) {
            // Variable contains CodeFrames - don't check anything
            return
          }
        }
      }
    }

    // if we check a CodeFrame, expand it and load children
    if(node.type == 'Codeframe' && isChecked && autoExpandVariables) {
      setExpanded(prev => [...prev, itemId])
      if(!node.children || node.children.length === 0 || (node.children.length === 1 && node.children[0].type === 'Loading')) {
        const loadedChildren = await loadCodesForVariable(node)
        useCodeFrameNode = {
          ...node,
          children: loadedChildren,
        }
      }
    }

    setChecked(prev => {
      if(node.type == 'Variable') {
        const affectedNodeIds = getAllDescendantIds(useNode, true)
        if(isChecked) {
          // If single variable mode is enabled, clear all other variables first
          if (singleVariable) {
            const targetVariableId = getNodeID(useNode)
            const filteredPrev = prev.filter(id => {
              // Find which variable this checked node belongs to
              const nodeFromMap = nodeMap[id]
              if (!nodeFromMap) return false
              
              if (nodeFromMap.type === 'Variable') {
                return getNodeID(nodeFromMap) === targetVariableId
              } else if (nodeFromMap.type === 'Code' || nodeFromMap.type === 'Net' || nodeFromMap.type === 'Arith') {
                return codeParents[id] === targetVariableId
              }
              return false
            })
            return [...filteredPrev, ...affectedNodeIds]
          }
          return [...prev, ...affectedNodeIds]
        } else {
          return prev.filter(id => affectedNodeIds.includes(id) ? false : true)
        }
      } else if(node.type == 'Code') {
        let affectedNodeIds = [getNodeID(node)]
        const parentNode = nodeMap[codeParents[getNodeID(node)]]
        if(isChecked) {
          if(!prev.includes(getNodeID(parentNode))) {
            affectedNodeIds = [getNodeID(parentNode), getNodeID(node)]
          }
          
          // If single variable mode is enabled, clear all other variables first
          if (singleVariable) {
            const targetVariableId = getNodeID(parentNode)
            const filteredPrev = prev.filter(id => {
              // Find which variable this checked node belongs to
              const nodeFromMap = nodeMap[id]
              if (!nodeFromMap) return false
              
              if (nodeFromMap.type === 'Variable') {
                return getNodeID(nodeFromMap) === targetVariableId
              } else if (nodeFromMap.type === 'Code' || nodeFromMap.type === 'Net' || nodeFromMap.type === 'Arith') {
                return codeParents[id] === targetVariableId
              }
              return false
            })
            return [...filteredPrev, ...affectedNodeIds]
          }
          return [...prev, ...affectedNodeIds]
        } else {
          const hasAnyOtherChildrenChecked = parentNode.children?.some(child => {
            if(getNodeID(node) == getNodeID(child)) return false
            return prev.includes(getNodeID(child))
          })

          if(!hasAnyOtherChildrenChecked) {
            affectedNodeIds = [getNodeID(parentNode), getNodeID(node)]
          }

          return prev.filter(id => affectedNodeIds.includes(id) ? false : true)
        }
      } else if(node.type == 'Net' || node.type == 'Arith') {
        // Net and Arith behave exactly like Code - they're leaf nodes with parent management
        let affectedNodeIds = [getNodeID(node)]
        const parentNode = nodeMap[codeParents[getNodeID(node)]]
        if(isChecked) {
          if(!prev.includes(getNodeID(parentNode))) {
            affectedNodeIds = [getNodeID(parentNode), getNodeID(node)]
          }

          // If single variable mode is enabled, clear all other variables first
          if (singleVariable) {
            const targetVariableId = getNodeID(parentNode)
            const filteredPrev = prev.filter(id => {
              // Find which variable this checked node belongs to
              const nodeFromMap = nodeMap[id]
              if (!nodeFromMap) return false

              if (nodeFromMap.type === 'Variable' || nodeFromMap.type === 'Codeframe') {
                return getNodeID(nodeFromMap) === targetVariableId
              } else if (nodeFromMap.type === 'Code' || nodeFromMap.type === 'Net' || nodeFromMap.type === 'Arith') {
                return codeParents[id] === targetVariableId
              }
              return false
            })
            return [...filteredPrev, ...affectedNodeIds]
          }
          return [...prev, ...affectedNodeIds]
        } else {
          const hasAnyOtherChildrenChecked = parentNode.children?.some(child => {
            if(getNodeID(node) == getNodeID(child)) return false
            return prev.includes(getNodeID(child))
          })

          if(!hasAnyOtherChildrenChecked) {
            affectedNodeIds = [getNodeID(parentNode), getNodeID(node)]
          }

          return prev.filter(id => affectedNodeIds.includes(id) ? false : true)
        }
      } else if(node.type == 'Codeframe') {
        // CodeFrame checking logic - similar to Variable, but CodeFrames always contain Codes
        const affectedNodeIds = getAllDescendantIds(useCodeFrameNode, true)
        if(isChecked) {
          // If single variable mode is enabled, clear all other variables/codeframes first
          if (singleVariable) {
            const targetCodeFrameId = getNodeID(useCodeFrameNode)
            const filteredPrev = prev.filter(id => {
              const nodeFromMap = nodeMap[id]
              if (!nodeFromMap) return false

              if (nodeFromMap.type === 'Codeframe') {
                return getNodeID(nodeFromMap) === targetCodeFrameId
              } else if (nodeFromMap.type === 'Code' || nodeFromMap.type === 'Net' || nodeFromMap.type === 'Arith') {
                return codeParents[id] === targetCodeFrameId
              }
              return false
            })
            return [...filteredPrev, ...affectedNodeIds]
          }
          return [...prev, ...affectedNodeIds]
        } else {
          return prev.filter(id => affectedNodeIds.includes(id) ? false : true)
        }
      } else {
        return prev
      }
    })
  }

  const onNodeSelectToggle = async (itemId: string, node: GenNode, isSelected: boolean) => {
    if (node.type === 'Variable' && isSelected && autoCheckOnSelect) {
      onNodeCheckedToggle(itemId, node, true)
    }
  }

  const resetChecked = () => {
    setChecked([])
  }

  // returns an array of GenNodes - includes Variables and CodeFrames that have some
  // checked codes inside - might not be all of the codes for that variable/codeframe
  const selectedVariables = useMemo(() => {
    if (checked.length === 0 || !nodes) return []

    const checkedItems: GenNode[] = []
    processNodes(nodes, (node) => {
      if(node.type === 'Variable') {
        if(!checked.includes(getNodeID(node))) return node
        const allChildCodes = codes[getNodeID(node)] || []
        const checkedChildCodes = allChildCodes.filter(code => checked.includes(getNodeID(code)))
        const checkedVariable = {
          ...node,
          children: checkedChildCodes,
        }
        checkedItems.push(checkedVariable)
        return node
      } else if(node.type === 'Codeframe') {
        if(!checked.includes(getNodeID(node))) return node
        const allChildCodes = codes[getNodeID(node)] || []
        const checkedChildCodes = allChildCodes.filter(code => checked.includes(getNodeID(code)))
        const checkedCodeFrame = {
          ...node,
          children: checkedChildCodes,
        }
        checkedItems.push(checkedCodeFrame)
        return node
      } else {
        return node
      }
    })

    return checkedItems
  }, [checked, nodes, codes])

  useEffect(() => {
    // Don't reset if we have initial values that will be restored
    if (initialVariableNames.length > 0 || initialFolderPath) {
      return
    }
    setExpanded([])
    setChecked([])
    setCodes({})
    setCodeParents({})
    codesGeneration.current += 1
    codeLoads.current.clear()
    loadedCodes.current.clear()
    pendingTicks.current.clear()
    setLoadingCodes(new Set())
    hasInitializedExpansion.current = false
  }, [
    customer,
    job,
    driver,
  ])

  // Auto-expand top-level folders and initial variables when data loads
  useEffect(() => {
    if (!nodes || nodes.length === 0 || !data) {
      return
    }

    // Only run this effect once when data is first loaded
    if (hasInitializedExpansion.current) {
      return
    }

    const nodesToExpand: string[] = []
    const nodesToCheck: string[] = []

    // The folders the job's variable tree file marks open (shared with the
    // construct builder's rail).
    nodesToExpand.push(...initiallyOpenFolderIds(nodes))

    // Handle initial variable names if provided
    if (initialVariableNames.length > 0) {
      for (const varName of initialVariableNames) {
        // First try to use folder path if available, otherwise fall back to searching
        if (initialFolderPath) {
          const ancestorIds = getAncestorFolderIdsFromPath(nodes, initialFolderPath)
          nodesToExpand.push(...ancestorIds)
        } else {
          const ancestorIds = getAncestorFolderIds(nodes, varName)
          nodesToExpand.push(...ancestorIds)
        }

        // Find the variable node and check it
        const variableNode = findNode(nodes, (node) => node.type === 'Variable' && node.value1 === varName)
        if (variableNode) {
          nodesToCheck.push(getNodeID(variableNode))
        }
      }
    }

    if (nodesToExpand.length > 0) {
      setExpanded(prev => {
        const uniqueIds = new Set([...prev, ...nodesToExpand])
        return Array.from(uniqueIds)
      })
    }

    if (nodesToCheck.length > 0) {
      setChecked(prev => {
        const uniqueIds = new Set([...prev, ...nodesToCheck])
        return Array.from(uniqueIds)
      })
    }

    hasInitializedExpansion.current = true
  }, [nodes, data, initialVariableNames, initialFolderPath])

  useEffect(() => {
    onSelectedVariablesChange(selectedVariables, nodeMap)
  }, [selectedVariables, nodeMap])

  return {
    nodes,
    codes,
    expanded,
    checked,
    setChecked,
    setExpanded,
    nodeMap,
    selectedVariables,
    onNodeExpandToggle,
    onNodeSelectToggle,
    onNodeCheckedToggle,
    getCheckboxState,
    resetChecked,
    isLoading,
    isError,
    error,
    loadCodesForVariable,
  }
}

export default useVariableTree